import { useMemo } from 'react';
import useSWR from 'swr';
import { sdk } from 'sdk';
import { revalidateOptions, useCart } from 'frontastic';

export interface RuleResult {
  name: string;
  passed: boolean;
}

export interface ApprovalEvaluationResult {
  requiresApproval: boolean;
  results: RuleResult[];
}

const DEFAULT_RESULT: ApprovalEvaluationResult = { requiresApproval: false, results: [] };

const useApprovalEvaluation = () => {
  const { cart } = useCart();

  const cartId = cart?.cartId;
  const lineItemsCount = cart?.lineItems?.length ?? 0;
  const totalCentAmount = cart?.sum?.centAmount ?? 0;
  const shippingMethodId = cart?.shippingInfo?.shippingMethodId;

  // Revalidate when anything the rules may depend on changes (items, total, shipping method).
  const swrKey = useMemo(
    () =>
      cartId && lineItemsCount > 0
        ? ['cart/evaluateApprovalRules', cartId, totalCentAmount, shippingMethodId ?? '']
        : null,
    [cartId, lineItemsCount, totalCentAmount, shippingMethodId],
  );

  const { data, isValidating } = useSWR(
    swrKey,
    async () => {
      const response = await sdk.callAction<ApprovalEvaluationResult>({
        actionName: 'cart/evaluateApprovalRules',
      });

      if (response.isError) {
        return DEFAULT_RESULT;
      }

      const result = response.data as ApprovalEvaluationResult | undefined;
      return {
        requiresApproval: !!result?.requiresApproval,
        results: Array.isArray(result?.results) ? (result!.results as RuleResult[]) : [],
      } satisfies ApprovalEvaluationResult;
    },
    {
      ...revalidateOptions,
      dedupingInterval: 2000,
    },
  );

  return useMemo<ApprovalEvaluationResult & { isLoading: boolean }>(
    () => ({
      requiresApproval: data?.requiresApproval ?? false,
      results: data?.results ?? [],
      isLoading: !!swrKey && !data && isValidating,
    }),
    [data, swrKey, isValidating],
  );
};

export default useApprovalEvaluation;
